import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { timeStringToDecimalHours as _timeStringToDecimalHours } from '../utils/TimeUtils';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  DateRange as RangeIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Today as TodayIcon,
  CalendarViewDay as DayIcon,
  ChevronLeft,
  ChevronRight,
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { productionOrderService } from '../services/productionOrderService';
import { schedulingService } from '../services/schedulingService';
import schedulerService, {
  OptimizationResult as _OptimizationResult,
} from '../services/schedulerService';
import SchedulerStartDialog from '../components/SchedulerStartDialog';
// Conflict analysis now comes from the backend API
interface OrderConflictAnalysis {
  orderId: number;
  hasConflicts: boolean;
  conflictCount: number;
  highestSeverity: 'none' | 'warning' | 'error' | 'critical';
  conflicts: any[];
  suggestedResolutions: string[];
}
import { productionLineService, ProductionLine } from '../services/productionLineService';
import { UiOrder } from '../mappers/productionOrderMapper';
import MultiLineGantt from '../components/MultiLineGantt';
import {
  formatDecimalHours,
  calculateEndTime as _calculateEndTime,
  formatTimeForDisplay,
} from '../utils/TimeUtils';
import { usePermissions } from '../contexts/PermissionsContext';
import { formatApiErrorForUser } from '../utils/apiRequestError';
import './ProductionSchedule.css';

type ViewMode = 'Day' | 'Rolling';

interface GanttTask {
  id: string;
  name: string;
  start: string;
  end: string; // ⬅ required
  progress: number;
  dependencies?: string;
  custom_class?: string;
  estimatedHours?: number;
  conflictAnalysis?: OrderConflictAnalysis;
  production_line_id?: number;
  production_line_name?: string;
  // Time properties for proper positioning
  startTime?: string;
  endTime?: string;
  // Unified spanning properties
  is_split_order?: boolean;
  spanning_type?: 'break' | 'day_end';
  part?: 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6' | 'p7' | 'p8' | 'p9' | 'p10' | 'p11' | 'p12'; // Indicates which part of a split order this task represents (p1=first part, p2=second part, etc.)
  first_portion_minutes?: number;
  second_portion_minutes?: number;
  second_portion_date?: string;
  second_portion_start_time?: string;
  // Legacy break-spanning properties (for migration)
  pre_break_minutes?: number;
  post_break_minutes?: number;
  break_pause_start?: string;
  break_pause_end?: string;
}

const ProductionSchedule: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [productionOrders, setProductionOrders] = useState<UiOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize state from URL parameters
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const urlViewMode = searchParams.get('view') as ViewMode;
    return ['Day', 'Rolling'].includes(urlViewMode) ? urlViewMode : 'Rolling';
  });
  const [rollingDays, setRollingDays] = useState(() => {
    const urlDays = searchParams.get('days');
    return urlDays ? Math.max(7, Math.min(90, parseInt(urlDays))) : 30;
  });
  // Local input value for days - only apply on submit to avoid refresh on every keystroke
  const [rollingDaysInput, setRollingDaysInput] = useState(() => {
    const urlDays = searchParams.get('days');
    const days = urlDays ? Math.max(7, Math.min(90, parseInt(urlDays))) : 30;
    return String(days);
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const urlDate = searchParams.get('date');
    if (urlDate) {
      // Handles YYYY-MM-DD format and avoids timezone issues from new Date('YYYY-MM-DD')
      const parts = urlDate.split('-').map(Number);
      if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    // If no date in URL or format is wrong, use today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight to be consistent
    return today;
  });
  const [ganttViewMode, setGanttViewMode] = useState<'day' | 'multi'>(() => {
    return viewMode === 'Day' ? 'day' : 'multi';
  });

  // Dialog states
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UiOrder | null>(null);
  const [selectedTaskConflicts, setSelectedTaskConflicts] = useState<OrderConflictAnalysis | null>(
    null
  );
  const [loadingTaskConflicts, setLoadingTaskConflicts] = useState(false);

  // Schedule statistics
  const [scheduleStats, setScheduleStats] = useState({
    totalOrders: 0,
    scheduledHours: 0,
    availableHours: 0,
    utilizationRate: 0,
    overdueOrders: 0,
  });

  // Available hours cache for the current view
  const [availableHoursCache, setAvailableHoursCache] = useState<Map<string, number>>(new Map());
  const [workersCache, setWorkersCache] = useState<Map<string, number>>(new Map());
  const [isActiveCache, setIsActiveCache] = useState<Map<string, boolean>>(new Map());
  const [workingHoursConfig, setWorkingHoursConfig] = useState<
    Map<
      string,
      {
        startTime: string;
        endTime: string;
        breakStartTime: string;
        breakEndTime: string;
        workers: number;
      }
    >
  >(new Map());

  // Toast notification state for conflicts
  const [conflictToast, setConflictToast] = useState<{
    open: boolean;
    conflictCount: number;
  }>({ open: false, conflictCount: 0 });

  // Scheduler State
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerDialogOpen, setSchedulerDialogOpen] = useState(false);

  // Refreshing state for overlay (separate from initial loading)
  const [refreshing, setRefreshing] = useState(false);

  // Production lines state
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);
  const [selectedProductionLineId, setSelectedProductionLineId] = useState<number | null>(null);

  useEffect(() => {
    loadScheduleData();
    loadProductionLines();
  }, [viewMode, rollingDays, selectedDate]);

  // Sync rollingDaysInput when rollingDays changes (e.g. after submit)
  useEffect(() => {
    setRollingDaysInput(String(rollingDays));
  }, [rollingDays]);

  const loadProductionLines = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const lines = await productionLineService.getActiveProductionLines(today, today);
      setProductionLines(lines);
    } catch (error) {
      console.warn('Failed to load production lines:', error);
      setProductionLines([]);
    }
  };

  const loadScheduleData = async (isRefresh = false, silent = false) => {
    if (!silent) {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
    }
    try {
      // Calculate the actual date range we need based on current view
      const ganttDates = getGanttDateRange();
      const startDate = ganttDates[0];
      const endDate = ganttDates[ganttDates.length - 1];

      // Add some buffer for unscheduled and past due orders
      const bufferStartDate = new Date(startDate);
      bufferStartDate.setDate(bufferStartDate.getDate() - 30); // 30 days before
      const bufferEndDate = new Date(endDate);
      bufferEndDate.setDate(bufferEndDate.getDate() + 30); // 30 days after

      const startDateStr = bufferStartDate.toISOString().split('T')[0];
      const endDateStr = bufferEndDate.toISOString().split('T')[0];

      // STEP 1: Load working hours data FIRST
      const dateStrings = ganttDates.map((date) => date.toISOString().split('T')[0]);

      let totalAvailableHours = 0;
      try {
        // Make single batch call to get all working hours data at once
        const availableHoursData = await schedulingService.getBatchAvailableHours(dateStrings);

        totalAvailableHours = availableHoursData.reduce(
          (sum, data) => sum + data.available_hours,
          0
        );

        // Update the caches for MultiLineGantt using the enhanced batch data
        const newHoursCache = new Map<string, number>();
        const newWorkersCache = new Map<string, number>();
        const newIsActiveCache = new Map<string, boolean>();
        const newWorkingHoursConfig = new Map<
          string,
          {
            startTime: string;
            endTime: string;
            breakStartTime: string;
            breakEndTime: string;
            workers: number;
          }
        >();

        availableHoursData.forEach((data) => {
          newHoursCache.set(data.date, data.available_hours);
          newWorkersCache.set(data.date, data.workers || 12);
          newIsActiveCache.set(data.date, data.is_active);

          // Set working hours config - only include break times for active days
          newWorkingHoursConfig.set(data.date, {
            startTime: data.start_time || '08:00:00',
            endTime: data.end_time || '17:00:00',
            breakStartTime: data.is_active ? data.break_start_time || '12:00:00' : '',
            breakEndTime: data.is_active ? data.break_end_time || '13:00:00' : '',
            workers: data.workers || 12,
          });
        });

        setAvailableHoursCache(newHoursCache);
        setWorkersCache(newWorkersCache);
        setIsActiveCache(newIsActiveCache);
        setWorkingHoursConfig(newWorkingHoursConfig);
      } catch (error) {
        console.warn('Failed to load batch available hours, using default 8 hours per day:', error);
        totalAvailableHours = dateStrings.length * 8; // Fallback to 8 hours per day

        // Set fallback cache
        const fallbackCache = new Map<string, number>();
        const fallbackWorkingHoursConfig = new Map<
          string,
          {
            startTime: string;
            endTime: string;
            breakStartTime: string;
            breakEndTime: string;
            workers: number;
          }
        >();

        dateStrings.forEach((date) => {
          fallbackCache.set(date, 8);
          fallbackWorkingHoursConfig.set(date, {
            startTime: '08:00:00',
            endTime: '17:00:00',
            breakStartTime: '12:00:00',
            breakEndTime: '13:00:00',
            workers: 12,
          });
        });

        setAvailableHoursCache(fallbackCache);
        setWorkingHoursConfig(fallbackWorkingHoursConfig);
      }

      // STEP 2: Load production orders for the calculated date range
      const allOrders = await productionOrderService.getCalendarView(startDateStr, endDateStr);

      // Filter out cancelled orders and completed orders in the future
      // Completed orders should only show in the past or today
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize to midnight for date comparison

      const filteredOrders = allOrders
        .filter((order) => {
          // Exclude cancelled orders
          if (order.status === 'cancelled') {
            return false;
          }

          // Exclude completed orders scheduled for future dates
          if (order.status === 'completed') {
            // For split orders, check all portion dates
            const schedulePortions = (order as any).schedule_portions as
              | Array<{
                  portion_number: number;
                  scheduled_date: string;
                  scheduled_time: string;
                  duration_minutes: number;
                  estimated_production_minutes?: number;
                }>
              | undefined;

            if (order.is_split_order && schedulePortions && schedulePortions.length > 0) {
              // Check if any portion is scheduled for a future date
              // Parse dates as local dates (YYYY-MM-DD format) to avoid timezone issues
              const hasFuturePortion = schedulePortions.some((portion) => {
                if (!portion.scheduled_date) return false;
                // Parse YYYY-MM-DD as local date (same way we create 'today')
                const dateParts = portion.scheduled_date.split('-').map(Number);
                if (dateParts.length !== 3) return false;
                const portionDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                portionDate.setHours(0, 0, 0, 0);
                return portionDate > today;
              });
              // If any portion is in the future, exclude the entire order
              if (hasFuturePortion) {
                return false;
              }
            } else if (order.planned_production_date) {
              // For regular orders, check the planned production date
              const plannedDate = new Date(order.planned_production_date);
              plannedDate.setHours(0, 0, 0, 0); // Normalize to midnight for date comparison
              // Only include if planned date is today or in the past
              if (plannedDate > today) {
                return false;
              }
            }
          }

          return true;
        })
        .sort((a, b) => {
          const dateA = new Date(a.planned_production_date || a.due_date || a.created_at);
          const dateB = new Date(b.planned_production_date || b.due_date || b.created_at);
          return dateA.getTime() - dateB.getTime();
        });

      setProductionOrders(filteredOrders);

      // STEP 3: Calculate stats using the EXACT date range displayed in the table
      // Normalize dates to midnight for accurate comparison
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(23, 59, 59, 999); // Include entire end date

      const rangeOrders = filteredOrders.filter((order) => {
        if (!order.planned_production_date) return false;
        const orderDate = new Date(order.planned_production_date);
        // Normalize to midnight for comparison
        orderDate.setHours(0, 0, 0, 0);
        return orderDate >= normalizedStartDate && orderDate <= normalizedEndDate;
      });

      // Calculate worker-scaled scheduled hours using the loaded workers cache
      const scheduledHours = Number(
        rangeOrders.reduce((sum, order) => {
          let hours = Number(order.estimated_production_hours) || 0;

          // Apply worker scaling if order has a planned production date
          if (order.planned_production_date) {
            const dateKey = order.planned_production_date;
            const workerCount = workersCache.get(dateKey) || 12;

            // Apply worker scaling: actualTime = baseTime * (12 / actualWorkers)
            if (workerCount !== 12) {
              hours = hours * (12 / workerCount);
            }
          }

          return sum + hours;
        }, 0)
      );

      const overdueOrders = rangeOrders.filter((order) => {
        if (!order.due_date) return false;
        return new Date(order.due_date) < new Date() && order.status === 'pending';
      }).length;

      const utilizationRate =
        totalAvailableHours > 0 ? (scheduledHours / totalAvailableHours) * 100 : 0;

      setScheduleStats({
        totalOrders: rangeOrders.length,
        scheduledHours,
        availableHours: totalAvailableHours,
        utilizationRate,
        overdueOrders,
      });
    } catch (err) {
      setError('Failed to load production schedule');
      console.error('Error loading schedule:', err);
    } finally {
      if (!silent) {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    }
  };

  // Helper function to get the date range for the Gantt chart
  const getGanttDateRange = () => {
    if (viewMode === 'Day') {
      // Single day view
      return [new Date(selectedDate)];
    }

    // Rolling view - show N consecutive days starting from current date
    const dates: Date[] = [];
    for (let i = 0; i < rollingDays; i++) {
      const date = new Date(selectedDate);
      date.setDate(selectedDate.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // Create memoized order lookup map
  const orderById = useMemo(() => {
    const map = new Map<string, UiOrder>();
    for (const order of productionOrders) {
      map.set(String(order.production_order_id), order);
    }
    return map;
  }, [productionOrders]);

  // Helper function to convert order to task using original hours (no worker scaling for display)
  // Helper function to calculate end time from start time and duration in minutes
  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    if (!startTime) return startTime;

    // Handle datetime format (e.g., "1970-01-01T11:45:00.000Z")
    if (startTime.includes('T')) {
      const date = new Date(startTime);
      const endDate = new Date(date.getTime() + durationMinutes * 60000);
      return endDate.toISOString();
    }

    // Handle time format (e.g., "11:45:00")
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDecimalHours = hours + (minutes || 0) / 60;
    const endDecimalHours = startDecimalHours + durationMinutes / 60;

    const endHours = Math.floor(endDecimalHours);
    const endMinutes = Math.round((endDecimalHours - endHours) * 60);
    const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}:00`;

    return endTime;
  };

  const orderToTask = (order: UiOrder): GanttTask | null => {
    // Only create tasks for orders that have a planned production date
    // Don't fall back to due_date for timeline display
    const start = order.planned_production_date;

    if (!start) {
      return null;
    }

    const startYmd = /^\d{4}-\d{2}-\d{2}$/.test(start)
      ? start
      : new Date(start).toISOString().slice(0, 10);

    // Use original estimated hours for visual display (no worker scaling)
    // This ensures consistency between visual display and conflict analysis
    const estimatedHours = order.estimated_production_hours ?? 4;

    // Parse the planned production time to get the actual start time
    // let startTime = '08:00:00'; // Default to 8 AM if no time specified
    // if (order.planned_production_time) {
    //   if (typeof order.planned_production_time === 'string') {
    //     // Handle both time-only strings (08:00:00) and datetime strings (1970-01-01T08:00:00.000Z)
    //     let timePart = order.planned_production_time;
    //     if (timePart.includes('T')) {
    //       // Extract time part from datetime string
    //       timePart = timePart.split('T')[1].split('.')[0]; // Remove timezone and milliseconds
    //     }
    //     startTime = timePart;
    //   }
    // }

    // Calculate end time based on start time and estimated hours
    // const [startHours, startMinutes] = startTime.split(':').map(Number);
    // const startDecimalHours = startHours + (startMinutes || 0) / 60;
    // const endDecimalHours = startDecimalHours + estimatedHours;

    // const endHours = Math.floor(endDecimalHours);
    // const endMinutes = Math.round((endDecimalHours - endHours) * 60);
    // const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}:00`;

    const id = String(order.production_order_id);

    // Handle split order naming - detect if this is a split order by checking if production_order_id has been modified
    let orderIdForDisplay = order.production_order_id;
    let splitSuffix = '';

    // Check if this is a split order by looking at the production_order_id
    // Split orders have IDs like 490001, 490002 (original order 49 with suffixes 0001, 0002)
    if (order.production_order_id > 100000) {
      // Extract original order ID and split number
      const orderIdStr = String(order.production_order_id);
      if (orderIdStr.length >= 5) {
        const originalOrderId = parseInt(orderIdStr.slice(0, -4)); // Remove last 4 digits
        const splitNumber = orderIdStr.slice(-4); // Last 4 digits

        if (splitNumber === '0001') {
          orderIdForDisplay = originalOrderId;
          splitSuffix = '/P1';
        } else if (splitNumber === '0002') {
          orderIdForDisplay = originalOrderId;
          splitSuffix = '/P2';
        }
      }
    }

    const name = `#${orderIdForDisplay}${splitSuffix} - ${order.product_name || 'Unknown Product'}`;
    const progress = order.status === 'completed' ? 100 : order.status === 'in_progress' ? 50 : 0;

    // Calculate proper end time based on duration
    let endYmd = startYmd;
    if (order.planned_production_time && estimatedHours > 0) {
      // Calculate end time by adding duration to start time
      const startTime = order.planned_production_time;
      let startDecimalHours = 0;

      if (startTime.includes('T')) {
        // Handle datetime format (e.g., "1970-01-01T11:35:00.000Z")
        const date = new Date(startTime);
        startDecimalHours = date.getUTCHours() + date.getUTCMinutes() / 60;
      } else {
        // Handle time format (e.g., "11:35:00")
        const [hours, minutes] = startTime.split(':').map(Number);
        startDecimalHours = hours + (minutes || 0) / 60;
      }

      const endDecimalHours = startDecimalHours + estimatedHours;

      // If the task goes past midnight, it should end on the next day
      if (endDecimalHours >= 24) {
        const nextDay = new Date(start);
        nextDay.setDate(nextDay.getDate() + 1);
        endYmd = nextDay.toISOString().slice(0, 10);
      }
    }

    // Calculate start and end times for proper Gantt positioning
    let startTime: string | undefined = undefined;
    let endTime: string | undefined = undefined;

    if (!order.planned_production_time) {
      // Orders with date but no time can't be displayed on Gantt chart
      // This is expected for partially scheduled orders, so use debug level
      if (order.planned_production_date) {
        // Has date but no time - likely partially scheduled
        return null;
      }
      // No date or time - completely unscheduled, skip silently
      return null;
    }

    if (!estimatedHours || estimatedHours <= 0) {
      console.error(
        `[ProductionSchedule] Order ${order.production_order_id} missing or invalid estimated_production_hours: ${estimatedHours}`
      );
      return null; // Don't create task without duration
    }

    const timeStr = order.planned_production_time;

    if (timeStr.includes('T')) {
      // Handle datetime format (e.g., "1970-01-01T11:35:00.000Z")
      const date = new Date(timeStr);
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      startTime = `${hours}:${minutes}:00`;
    } else {
      // Handle time format (e.g., "11:35:00")
      startTime = timeStr;
    }

    // Calculate end time
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const startDecimalHours = startHours + startMinutes / 60;
    const endDecimalHours = startDecimalHours + estimatedHours;

    const endHours = Math.floor(endDecimalHours);
    const endMinutes = Math.round((endDecimalHours - endHours) * 60);
    endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}:00`;

    return {
      id,
      name,
      start: startYmd,
      end: endYmd,
      progress,
      estimatedHours,
      // Use order's production_line_id if set, otherwise fall back to product's production_line_id, then default to 1
      production_line_id: (() => {
        const lineId = order.production_line_id || order.product_production_line_id || 1;
        return lineId;
      })(),
      production_line_name: order.production_line_name || 'Line 1',
      // Time properties for proper positioning
      startTime: startTime!,
      endTime: endTime!,
      // Break-spanning properties
      is_split_order: order.is_split_order,
      pre_break_minutes: order.pre_break_minutes,
      post_break_minutes: order.post_break_minutes,
      break_pause_start: order.break_pause_start,
      break_pause_end: order.break_pause_end,
      custom_class:
        order.status === 'completed'
          ? 'gantt-task-completed'
          : order.status === 'in_progress'
            ? 'gantt-task-in-progress'
            : order.priority_level === 'urgent'
              ? 'gantt-task-urgent'
              : order.priority_level === 'high'
                ? 'gantt-task-high'
                : 'gantt-task-pending',
    };
  };

  // Create safe tasks array with unique IDs, today anchor, and conflict analysis
  const tasks = useMemo(() => {
    const taskArray: GanttTask[] = [];
    const seen = new Set<string>();

    // Convert production orders to the format expected by conflict analysis
    // const ordersForAnalysis = productionOrders
    //   .filter((order) => order.planned_production_date && order.planned_production_time)
    //   .map((po) => ({
    //     production_order_id: po.production_order_id,
    //     product_name: po.product_name,
    //     planned_production_date: po.planned_production_date,
    //     planned_production_time: po.planned_production_time,
    //     estimated_production_hours: po.estimated_production_hours,
    //     priority_level: po.priority_level,
    //     status: po.status,
    //   }));

    // Count total conflicts from API data for the toast notification
    const totalConflicts = productionOrders.reduce((sum, order) => {
      const conflictAnalysis = (order as any).conflictAnalysis;
      return sum + (conflictAnalysis?.conflictCount || 0);
    }, 0);

    // Update conflict toast if there are conflicts
    if (totalConflicts > 0) {
      setConflictToast({ open: true, conflictCount: totalConflicts });
    } else {
      setConflictToast({ open: false, conflictCount: 0 });
    }

    for (const order of productionOrders) {
      // Check if this is a split order with dynamic portions
      const schedulePortions = (order as any).schedule_portions as
        | Array<{
            portion_number: number;
            scheduled_date: string;
            scheduled_time: string;
            duration_minutes: number;
            estimated_production_minutes?: number;
            production_line_id?: number;
            production_line_name?: string;
          }>
        | undefined;

      if (order.is_split_order && schedulePortions && schedulePortions.length > 1) {
        // Dynamic split order - create tasks for all portions
        // Create a task for each portion
        for (let i = 0; i < schedulePortions.length; i++) {
          const portion = schedulePortions[i];

          // Skip portions without required data
          if (!portion.scheduled_date || !portion.scheduled_time) {
            continue;
          }

          const partLabels: Array<
            'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6' | 'p7' | 'p8' | 'p9' | 'p10' | 'p11' | 'p12'
          > = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'p9', 'p10', 'p11', 'p12'];
          const partLabel = partLabels[i] || (`p${i + 1}` as any);

          const portionTask = orderToTask({
            ...order,
            estimated_production_hours: portion.duration_minutes / 60,
            planned_production_time: portion.scheduled_time,
            planned_production_date: portion.scheduled_date,
            production_line_id: portion.production_line_id ?? order.production_line_id,
            production_line_name: portion.production_line_name ?? order.production_line_name,
          });

          if (portionTask) {
            portionTask.part = partLabel;
            portionTask.id = `${order.production_order_id}`;
            portionTask.is_split_order = true;
            portionTask.name = `#${order.production_order_id}/${partLabel.toUpperCase()} - ${order.product_name || 'Unknown Product'}`;

            const taskKey = `${portionTask.id}-${partLabel}`;
            if (seen.has(taskKey)) continue;
            seen.add(taskKey);

            const conflictAnalysis = (order as any).conflictAnalysis;
            if (conflictAnalysis) {
              portionTask.conflictAnalysis = conflictAnalysis;
            }
            taskArray.push(portionTask);
          }
        }
      } else {
        // Regular order - create single task
        const task = orderToTask(order);
        if (!task) {
          continue;
        }
        if (seen.has(task.id)) {
          continue; // enforce unique IDs
        }
        seen.add(task.id);

        // Add conflict analysis to the task from API data
        const conflictAnalysis = (order as any).conflictAnalysis;
        if (conflictAnalysis) {
          task.conflictAnalysis = conflictAnalysis;
        }
        taskArray.push(task);
      }
    }

    // Add a hidden anchor task for today to ensure the highlight target exists
    const todayYmd = new Date().toISOString().slice(0, 10);
    taskArray.push({
      id: '__today_anchor__',
      name: '',
      start: todayYmd,
      end: todayYmd,
      progress: 0,
      custom_class: 'gantt-anchor-hidden',
    });

    return taskArray;
  }, [productionOrders, availableHoursCache, workersCache]);

  // Handle task click in Gantt chart
  const handleTaskClick = async (task: GanttTask) => {
    if (task.id === '__today_anchor__') return; // Ignore clicks on anchor
    const order = orderById.get(String(task.id));
    if (!order) return;

    setSelectedTask(order);
    setSelectedTaskConflicts(null);
    setOpenTaskDialog(true);

    // Analyze conflicts for this task using local data
    if (order.planned_production_date) {
      setLoadingTaskConflicts(true);
      try {
        // Convert production orders to the format expected by conflict analysis
        const ordersForAnalysis = productionOrders.map((po) => ({
          production_order_id: po.production_order_id,
          product_name: po.product_name,
          planned_production_date: po.planned_production_date,
          planned_production_time: po.planned_production_time,
          estimated_production_hours: po.estimated_production_hours,
          priority_level: po.priority_level,
          status: po.status,
        }));

        // Find the target order for analysis
        const targetOrder = ordersForAnalysis.find(
          (po) => po.production_order_id === order.production_order_id
        );

        if (targetOrder) {
          // Get conflict analysis from API data
          const conflictAnalysis = (targetOrder as any).conflictAnalysis;
          setSelectedTaskConflicts(conflictAnalysis || null);
        }
      } catch (error) {
        console.warn('Failed to analyze conflicts:', error);
      } finally {
        setLoadingTaskConflicts(false);
      }
    }
  };

  const handleTaskDateChange = async (taskId: string, start: Date, _end: Date) => {
    try {
      const orderId = parseInt(taskId);

      // Check if this is an unschedule operation (invalid date)
      const isUnschedule = isNaN(start.getTime());

      let updateData: any;

      if (isUnschedule) {
        updateData = {
          planned_production_date: null,
          planned_production_time: null,
        };
      } else {
        // Extract date and time separately
        // Use local date methods to avoid timezone issues
        const year = start.getFullYear();
        const month = String(start.getMonth() + 1).padStart(2, '0');
        const day = String(start.getDate()).padStart(2, '0');
        const hours = String(start.getHours()).padStart(2, '0');
        const minutes = String(start.getMinutes()).padStart(2, '0');

        const dateStr = `${year}-${month}-${day}`;
        const timeStr = `${hours}:${minutes}`;

        // Check if the selected date has an inactive override
        // If date is in cache and is_active is false, block scheduling
        if (isActiveCache.has(dateStr)) {
          const isDateActive = isActiveCache.get(dateStr);
          if (isDateActive === false) {
            setError(
              'Cannot schedule on a non-working day. This date has a schedule override marked as inactive.'
            );
            return;
          }
        }

        updateData = {
          planned_production_date: dateStr,
          planned_production_time: timeStr,
        };
      }

      // Optimistic update - update local state immediately
      setProductionOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.production_order_id === orderId
            ? {
                ...order,
                planned_production_date: updateData.planned_production_date ?? null,
                planned_production_time: updateData.planned_production_time ?? null,
              }
            : order
        )
      );

      // Update backend asynchronously
      await productionOrderService.updateProductionOrder(orderId, updateData);

      setSuccess(
        isUnschedule
          ? 'Production order unscheduled successfully'
          : `Production order scheduled for ${updateData.planned_production_date} at ${updateData.planned_production_time}`
      );

      // Reload schedule data to ensure unscheduled orders appear correctly
      // Silent refresh (no overlay) - data updates in background
      await loadScheduleData(true, true);
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to update schedule');
      // Revert optimistic update on error - silent reload to get fresh data
      loadScheduleData(false, true);
    }
  };

  const handleTaskProgressChange = async (taskId: string, progress: number) => {
    try {
      const orderId = parseInt(taskId);
      let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';

      if (progress === 100) {
        newStatus = 'completed';
      } else if (progress > 0) {
        newStatus = 'in_progress';
      }

      // Optimistic update - update local state immediately
      setProductionOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.production_order_id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Update backend asynchronously
      await productionOrderService.updateProductionOrderStatus(orderId, newStatus);
      setSuccess('Production order status updated successfully');
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to update status');
      // Revert optimistic update on error - reload to get fresh data
      loadScheduleData();
    }
  };

  // Update URL when state changes
  const updateURL = (newViewMode?: ViewMode, newDate?: Date, newDays?: number) => {
    const params = new URLSearchParams(searchParams);

    if (newViewMode !== undefined) {
      params.set('view', newViewMode);
    }
    if (newDate !== undefined) {
      params.set('date', newDate.toISOString().split('T')[0]);
    }
    if (newDays !== undefined && newViewMode === 'Rolling') {
      params.set('days', newDays.toString());
    }

    setSearchParams(params);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setGanttViewMode(mode === 'Day' ? 'day' : 'multi');
    updateURL(mode, selectedDate, rollingDays);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setSelectedDate(today);
    updateURL(viewMode, today, rollingDays);
    // Trigger stats recalculation after date change
    loadScheduleData();
  };

  const handleRollingDaysChange = (days: number) => {
    // Enforce min/max limits
    const minDays = 7;
    const maxDays = 90;
    const clampedDays = Math.max(minDays, Math.min(maxDays, days));
    setRollingDays(clampedDays);
    updateURL(viewMode, selectedDate, clampedDays);
  };

  const handleApplyRollingDays = () => {
    const parsed = parseInt(rollingDaysInput, 10);
    const clamped = isNaN(parsed) ? 7 : Math.max(7, Math.min(90, parsed));
    handleRollingDaysChange(clamped);
  };

  // Navigation handlers for Gantt chart
  const handleNavigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);

    if (viewMode === 'Day') {
      // Navigate by day
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else {
      // Navigate by rolling days
      newDate.setDate(newDate.getDate() + (direction === 'next' ? rollingDays : -rollingDays));
    }

    setSelectedDate(newDate);
    updateURL(viewMode, newDate, rollingDays);
  };

  // Get unscheduled tasks (orders without planned production date)
  const unscheduledTasks = useMemo(() => {
    return productionOrders
      .filter(
        (order) =>
          !order.planned_production_date &&
          order.status !== 'cancelled' &&
          order.status !== 'completed'
      )
      .map((order) =>
        orderToTask({ ...order, planned_production_date: new Date().toISOString().split('T')[0] })
      )
      .filter((task): task is GanttTask => task !== null);
  }, [productionOrders]);

  // Get past due tasks (orders scheduled for past dates that are not completed)
  const pastDueTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return productionOrders
      .filter((order) => {
        if (
          !order.planned_production_date ||
          order.status === 'completed' ||
          order.status === 'cancelled'
        ) {
          return false;
        }
        const scheduledDate = new Date(order.planned_production_date);
        scheduledDate.setHours(0, 0, 0, 0);
        return scheduledDate < today;
      })
      .map((order) => orderToTask(order))
      .filter((task): task is GanttTask => task !== null);
  }, [productionOrders]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'normal':
        return 'default';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatQuantity = (cases: number, units: number) => {
    if (cases > 0 && units > 0) {
      return `${cases} cases + ${units} units`;
    } else if (cases > 0) {
      return `${cases} cases`;
    } else if (units > 0) {
      return `${units} units`;
    }
    return '0';
  };

  // Handle refresh button click
  const handleRefreshClick = () => {
    loadScheduleData(true);
  };

  // Handle run scheduler optimization (called from the dialog's confirm button)
  const handleRunSchedulerOptimization = async (startDateTime: Date) => {
    setSchedulerDialogOpen(false);
    try {
      setSchedulerLoading(true);
      setRefreshing(true);
      setError(null);
      const result = await schedulerService.runOptimization(startDateTime);
      setSuccess(
        `Scheduler completed! Orders processed: ${result.ordersProcessed}, Scheduled: ${result.ordersScheduled ?? result.ordersScheduledOnTime ?? 0}, Score: ${result.optimizationScore}%`
      );
      await loadScheduleData(true);
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to run optimization');
    } finally {
      setSchedulerLoading(false);
      setRefreshing(false);
    }
  };

  // Handle toast close
  const handleToastClose = () => {
    setConflictToast({ open: false, conflictCount: 0 });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, position: 'relative' }}>
        {/* Loading Overlay */}
        {refreshing && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {schedulerLoading ? 'Running optimization...' : 'Refreshing data...'}
            </Typography>
          </Box>
        )}
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ScheduleIcon />
            Production Schedule
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {hasPermission('admin.production_schedule') && (
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/admin/schedule')}
                size="small"
              >
                Manage Schedule
              </Button>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                onChange={(e) => handleViewModeChange(e.target.value as ViewMode)}
                label="View Mode"
              >
                <MenuItem value="Day">
                  <Box display="flex" alignItems="center" gap={1}>
                    <DayIcon fontSize="small" />
                    Daily
                  </Box>
                </MenuItem>
                <MenuItem value="Rolling">
                  <Box display="flex" alignItems="center" gap={1}>
                    <RangeIcon fontSize="small" />
                    Rolling
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={() => handleNavigate('prev')}
                sx={{ bgcolor: 'action.hover' }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleNavigate('next')}
                sx={{ bgcolor: 'action.hover' }}
              >
                <ChevronRight />
              </IconButton>
            </Stack>
            {viewMode === 'Rolling' && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TextField
                  size="small"
                  label="Days"
                  type="number"
                  value={rollingDaysInput}
                  onChange={(e) => setRollingDaysInput(e.target.value)}
                  onBlur={() => {
                    const parsed = parseInt(rollingDaysInput, 10);
                    if (!isNaN(parsed)) {
                      const clamped = Math.max(7, Math.min(90, parsed));
                      setRollingDaysInput(String(clamped));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyRollingDays();
                  }}
                  inputProps={{ min: 7, max: 90 }}
                  error={(() => {
                    const p = parseInt(rollingDaysInput, 10);
                    return isNaN(p) || p < 7 || p > 90;
                  })()}
                  helperText={(() => {
                    const p = parseInt(rollingDaysInput, 10);
                    return isNaN(p) || p < 7 || p > 90 ? 'Days must be between 7 and 90' : '';
                  })()}
                  sx={{ width: 100 }}
                />
                <IconButton
                  size="small"
                  onClick={handleApplyRollingDays}
                  sx={{ bgcolor: 'action.hover' }}
                  title="Apply days"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            <Button variant="outlined" startIcon={<TodayIcon />} onClick={handleTodayClick}>
              Today
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshClick}>
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={schedulerLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={() => setSchedulerDialogOpen(true)}
              disabled={schedulerLoading}
            >
              {schedulerLoading ? 'Optimizing...' : 'Scheduler'}
            </Button>
          </Stack>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Schedule Statistics */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 0.5, '&:last-child': { pb: 0.5 } }}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 'bold', fontSize: '14px' }}
                >
                  {scheduleStats.totalOrders} Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 0.5, '&:last-child': { pb: 0.5 } }}>
                <Typography
                  variant="body2"
                  color="info.main"
                  sx={{ fontWeight: 'bold', fontSize: '14px' }}
                >
                  {scheduleStats.scheduledHours.toFixed(1)}h Scheduled Hrs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 0.5, '&:last-child': { pb: 0.5 } }}>
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ fontWeight: 'bold', fontSize: '14px' }}
                >
                  {scheduleStats.availableHours.toFixed(2)}h Available Hrs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 0.5, '&:last-child': { pb: 0.5 } }}>
                <Typography
                  variant="body2"
                  color={scheduleStats.utilizationRate > 100 ? 'error.main' : 'warning.main'}
                  sx={{ fontWeight: 'bold', fontSize: '14px' }}
                >
                  {scheduleStats.utilizationRate.toFixed(1)}% Utilization
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 0.5, '&:last-child': { pb: 0.5 } }}>
                <Typography
                  variant="body2"
                  color="error.main"
                  sx={{ fontWeight: 'bold', fontSize: '14px' }}
                >
                  {scheduleStats.overdueOrders} Overdue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gantt Chart */}
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <MultiLineGantt
            isActiveCache={isActiveCache}
            tasks={tasks}
            viewMode={ganttViewMode}
            currentDate={selectedDate}
            rollingDays={rollingDays}
            actualViewMode={viewMode}
            onTaskClick={handleTaskClick}
            onTaskDateChange={handleTaskDateChange}
            onTaskProgressChange={handleTaskProgressChange}
            onNavigate={handleNavigate}
            unscheduledTasks={unscheduledTasks}
            pastDueTasks={pastDueTasks}
            productionOrders={productionOrders}
            availableHours={availableHoursCache}
            workersCache={workersCache}
            workingHoursConfig={workingHoursConfig}
            productionLines={productionLines}
            selectedProductionLineId={selectedProductionLineId}
            onLineFilterChange={setSelectedProductionLineId}
          />
        </Paper>

        {/* Legend */}
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="h6" gutterBottom>
            Legend
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 12, bgcolor: '#4caf50', borderRadius: 1 }} />
                <Typography variant="body2">Completed</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 12, bgcolor: '#2196f3', borderRadius: 1 }} />
                <Typography variant="body2">In Progress</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 12, bgcolor: '#ff9800', borderRadius: 1 }} />
                <Typography variant="body2">Pending</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 12, bgcolor: '#f44336', borderRadius: 1 }} />
                <Typography variant="body2">Urgent Priority</Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Click on tasks to view details. Drag tasks to reschedule. Drag progress bars to update
            status.
          </Typography>
        </Paper>

        {/* Task Details Dialog */}
        <Dialog
          open={openTaskDialog}
          onClose={() => setOpenTaskDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle>Production Order Details</DialogTitle>
          <DialogContent
            sx={{
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 120px)',
            }}
          >
            {selectedTask && (
              <List>
                <ListItem>
                  <ListItemText
                    primary="Order ID"
                    secondary={`#${selectedTask.production_order_id}`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Product"
                    secondary={`${selectedTask.product_name} (${selectedTask.product_code})`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Quantity"
                    secondary={formatQuantity(
                      selectedTask.quantity_cases,
                      selectedTask.quantity_units
                    )}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Box component="span">
                        <Chip
                          label={selectedTask.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(selectedTask.status) as any}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
                {selectedTask.production_line_name && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Production Line"
                        secondary={selectedTask.production_line_name}
                      />
                    </ListItem>
                    <Divider />
                  </>
                )}
                <ListItem>
                  <ListItemText
                    primary="Priority"
                    secondary={
                      <Box component="span">
                        <Chip
                          label={selectedTask.priority_level.toUpperCase()}
                          color={getPriorityColor(selectedTask.priority_level) as any}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
                {(selectedTask.planned_production_date || selectedTask.planned_production_time) && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Scheduled"
                        secondary={
                          selectedTask.planned_production_date &&
                          selectedTask.planned_production_time
                            ? `${new Date(selectedTask.planned_production_date).toLocaleDateString()} ${formatTimeForDisplay(selectedTask.planned_production_time)}`
                            : selectedTask.planned_production_date
                              ? new Date(selectedTask.planned_production_date).toLocaleDateString()
                              : selectedTask.planned_production_time
                                ? formatTimeForDisplay(selectedTask.planned_production_time)
                                : 'Not set'
                        }
                      />
                    </ListItem>
                    <Divider />
                  </>
                )}
                {selectedTask.due_date && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Due Date"
                        secondary={new Date(selectedTask.due_date).toLocaleDateString()}
                      />
                    </ListItem>
                    <Divider />
                  </>
                )}
                {selectedTask.estimated_production_hours && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Estimated Duration"
                        secondary={formatDecimalHours(selectedTask.estimated_production_hours)}
                      />
                    </ListItem>
                    <Divider />
                  </>
                )}
                {selectedTask.planned_production_time &&
                  selectedTask.estimated_production_hours && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Planned End Time"
                          secondary={calculateEndTime(
                            selectedTask.planned_production_time,
                            selectedTask.estimated_production_hours
                          )}
                        />
                      </ListItem>
                      <Divider />
                    </>
                  )}
                {selectedTask.notes && (
                  <>
                    <ListItem>
                      <ListItemText primary="Notes" secondary={selectedTask.notes} />
                    </ListItem>
                    <Divider />
                  </>
                )}

                {/* Conflict Analysis Section */}
                <ListItem>
                  <ListItemText
                    primary="Scheduling Conflicts"
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {loadingTaskConflicts ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Analyzing conflicts...</Typography>
                          </Box>
                        ) : selectedTaskConflicts ? (
                          selectedTaskConflicts.hasConflicts ? (
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', color: 'error.main', mb: 1 }}
                              >
                                {selectedTaskConflicts
                                  ? `${selectedTaskConflicts.conflictCount} conflicts`
                                  : 'No conflicts'}
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                {selectedTaskConflicts.conflicts.map((conflict, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 2,
                                      p: 1,
                                      bgcolor: '#ffebee',
                                      borderRadius: 1,
                                      border: '1px solid #f44336',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 'bold', color: 'error.main', mb: 0.5 }}
                                    >
                                      {conflict.type === 'time_overlap'
                                        ? '⏰'
                                        : conflict.type === 'capacity_overflow'
                                          ? '📊'
                                          : conflict.type === 'resource_shortage'
                                            ? '⚠️'
                                            : conflict.type === 'priority_conflict'
                                              ? '⚡'
                                              : conflict.type === 'non_working_hours'
                                                ? '🕐'
                                                : '❌'}{' '}
                                      {conflict.type.replace('_', ' ').toUpperCase()}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      {conflict.message}
                                    </Typography>
                                    {conflict.details.suggestedAction && (
                                      <Typography
                                        variant="body2"
                                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                      >
                                        💡 {conflict.details.suggestedAction}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                              {selectedTaskConflicts.suggestedResolutions.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 1,
                                    bgcolor: '#e3f2fd',
                                    borderRadius: 1,
                                    border: '1px solid #2196f3',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}
                                  >
                                    💡 Suggested Resolutions:
                                  </Typography>
                                  <List dense>
                                    {selectedTaskConflicts.suggestedResolutions.map(
                                      (resolution, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemText
                                            primary={`• ${resolution}`}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'success.main' }}>
                              ✅ No conflicts detected
                            </Typography>
                          )
                        ) : selectedTask.planned_production_date ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Failed to load conflict analysis
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Order not scheduled - no conflicts to analyze
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTaskDialog(false)}>Close</Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                // Open production orders page in new window with selected order
                window.open(
                  `/production-orders?orderId=${selectedTask?.production_order_id}`,
                  '_blank'
                );
              }}
            >
              Edit Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Scheduler Start Dialog */}
        <SchedulerStartDialog
          open={schedulerDialogOpen}
          onClose={() => setSchedulerDialogOpen(false)}
          onConfirm={handleRunSchedulerOptimization}
          running={schedulerLoading}
        />

        {/* Conflict Toast Notification */}
        <Snackbar
          open={conflictToast.open}
          autoHideDuration={6000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleToastClose}
            severity="warning"
            sx={{ width: '100%' }}
            variant="filled"
          >
            {conflictToast.conflictCount} scheduling conflict
            {conflictToast.conflictCount > 1 ? 's' : ''} detected! Orders with red borders need to
            be rescheduled.
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductionSchedule;
